import React, { useEffect, useState } from 'react';
import './leadCaptureAnalytics.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const RANGES = [7, 30, 90];

function fmtNum(n) { return new Intl.NumberFormat('en-US').format(n || 0); }

export default function AdminLeadCaptureAnalytics() {
  const [range, setRange] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      setLoading(true);
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
        const res = await fetch(
          `${BACKEND_URL}/api/admin/analytics/lead-capture/overview?range=${range}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) {
          setData(json);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [range, refreshTick]);

  const refresh = () => setRefreshTick((t) => t + 1);

  const cleanupTestSeed = async () => {
    const msg = 'Delete all test/demo lead-capture analytics data?\n\n' +
      'Removes pages with id starting with "test-lc-" or "demo-lc-page-" ' +
      'and all related view/conversion/rollup events. Real member pages and ' +
      'real visitor events will not be touched.';
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await fetch(
        `${BACKEND_URL}/api/admin/analytics/lead-capture/cleanup-test-seed`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      const j = await res.json();
      if (j.ok) {
        const d = j.deleted || {};
        alert(`Cleaned up:\n  Pages: ${d.pages}\n  Views: ${d.views}\n  Conversions: ${d.conversions}\n  Rollups: ${d.rollups}`);
        refresh();
      } else {
        alert('Cleanup failed.');
      }
    } catch (e) {
      alert('Cleanup error: ' + e.message);
    }
  };

  return (
    <div className="lca" data-testid="admin-lc-analytics">
      <div className="lca-header">
        <h1>Lead Capture Analytics</h1>
        <p>
          Platform overview of member-owned lead capture pages. Privacy-conscious
          aggregates only — no raw visitor data, no IP storage.
        </p>
      </div>

      <div className="lca-rangebar" data-testid="lca-rangebar">
        {RANGES.map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={r === range ? 'is-active' : ''}
            data-testid={`lca-range-${r}`}
          >
            Last {r} days
          </button>
        ))}
        <button
          onClick={refresh}
          className=""
          data-testid="lca-refresh"
          style={{ marginLeft: 'auto' }}
        >
          Refresh
        </button>
        <button
          onClick={cleanupTestSeed}
          data-testid="lca-cleanup-test-seed-btn"
          title="Removes pages with id starting with test-lc- or demo-lc-page- and their events."
          style={{ borderColor: 'rgba(239,68,68,0.4)', color: '#fca5a5' }}
        >
          Cleanup test/demo data
        </button>
      </div>

      {loading && <div className="lca-empty">Loading…</div>}
      {error && <div className="lca-empty" data-testid="lca-error">Failed to load: {error}</div>}

      {data && !loading && (
        <>
          <div className="lca-grid lca-grid--4" style={{ marginBottom: 18 }}>
            <div className="lca-card"><div className="lca-stat">
              <span className="lca-stat-label">Tracked pages</span>
              <span className="lca-stat-value" data-testid="lca-tracked-pages">{fmtNum(data.tracked_pages)}</span>
              <span className="lca-stat-sub">Tracking enabled</span>
            </div></div>
            <div className="lca-card"><div className="lca-stat">
              <span className="lca-stat-label">Total views</span>
              <span className="lca-stat-value" data-testid="lca-total-views">{fmtNum(data.total_views)}</span>
              <span className="lca-stat-sub">All tracked pages · {range}d</span>
            </div></div>
            <div className="lca-card"><div className="lca-stat">
              <span className="lca-stat-label">Total leads</span>
              <span className="lca-stat-value" data-testid="lca-total-leads">{fmtNum(data.total_leads)}</span>
              <span className="lca-stat-sub">Conversions in window</span>
            </div></div>
            <div className="lca-card"><div className="lca-stat">
              <span className="lca-stat-label">Avg CVR</span>
              <span className="lca-stat-value" data-testid="lca-avg-cvr" style={{ color: 'var(--lca-gold)' }}>
                {data.avg_cvr_pct}%
              </span>
              <span className="lca-stat-sub">Leads / Views</span>
            </div></div>
          </div>

          <div className="lca-grid lca-grid--2">
            <div className="lca-card">
              <h3>Top pages by leads</h3>
              {data.top_pages_by_leads?.length > 0 ? (
                <table className="lca-table" data-testid="lca-top-pages-table">
                  <thead>
                    <tr><th>Page</th><th className="right">Views</th><th className="right">Leads</th><th className="right">CVR</th></tr>
                  </thead>
                  <tbody>
                    {data.top_pages_by_leads.map((p) => (
                      <tr key={p.page_id} data-testid={`lca-top-page-row-${p.page_id}`}>
                        <td>{p.title || p.page_id}</td>
                        <td className="right">{fmtNum(p.views)}</td>
                        <td className="right">{fmtNum(p.leads)}</td>
                        <td className="right" style={{ color: 'var(--lca-gold)' }}>{p.cvr_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ margin: 0, color: 'var(--lca-muted)', fontSize: 12 }}>No conversions in this window yet.</p>
              )}
            </div>

            <div className="lca-card">
              <h3>Top templates by CVR</h3>
              {data.top_templates_by_cvr?.length > 0 ? (
                <table className="lca-table" data-testid="lca-top-templates-table">
                  <thead>
                    <tr><th>Template</th><th className="right">Pages</th><th className="right">Views</th><th className="right">Leads</th><th className="right">CVR</th></tr>
                  </thead>
                  <tbody>
                    {data.top_templates_by_cvr.map((t) => (
                      <tr key={t.template_id} data-testid={`lca-top-tmpl-row-${t.template_id}`}>
                        <td><code style={{ fontSize: 11 }}>{t.template_id}</code></td>
                        <td className="right">{fmtNum(t.pages)}</td>
                        <td className="right">{fmtNum(t.views)}</td>
                        <td className="right">{fmtNum(t.leads)}</td>
                        <td className="right" style={{ color: 'var(--lca-gold)' }}>{t.cvr_pct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ margin: 0, color: 'var(--lca-muted)', fontSize: 12 }}>No template-tagged pages tracked yet.</p>
              )}
            </div>
          </div>

          <div className="lca-privacy">
            <strong>Privacy posture.</strong> No raw IP addresses are stored.
            No raw user-agent strings are stored. Visitor identities use
            daily-rotating one-way hashes. <code>DNT: 1</code> and{' '}
            <code>Sec-GPC: 1</code> are honoured — those requests are dropped
            before any data is written. Raw events expire automatically at
            180 days. Daily rollups are kept indefinitely. BBC Quick List
            uses non-custodial member-to-member payments via NowPayments /
            TRON TRC-20 — analytics data is never linked to wallets.
          </div>
        </>
      )}
    </div>
  );
}
